import { Copy, MagnifyingGlass, X } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { aiDraftInput, type AiDraftResponse } from '@brechoflow/contracts';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { usePermission } from '@/features/auth/session';
import { errorMessage } from '@/lib/api';
import { aiApi, isAiUnavailable } from './api';
import { Notice, REVIEW_NOTICE, textareaClasses } from './notices';

type Kind = 'novidades' | 'retorno';
type Channel = 'email' | 'whatsapp';

const KINDS = [
  { value: 'novidades', label: 'Novidades e peças novas' },
  { value: 'retorno', label: 'Convite para voltar à loja' },
];
const TONES = [
  { value: 'cordial', label: 'Cordial' },
  { value: 'objetivo', label: 'Objetivo' },
  { value: 'empatico', label: 'Acolhedor' },
];
const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'E-mail' },
];
const CHANNEL_NAME: Record<Channel, string> = { whatsapp: 'WhatsApp', email: 'e-mail' };

export function DraftsPanel({ onUnavailable }: { onUnavailable: () => void }) {
  const canFindCustomers = usePermission('customers.read');

  const [kind, setKind] = useState<Kind>('novidades');
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [tone, setTone] = useState('cordial');
  const [channel, setChannel] = useState<Channel>('whatsapp');
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<AiDraftResponse | null>(null);

  const run = useMutation({
    mutationFn: aiApi.draft,
    onSuccess: (d) => setDraft(d),
    onError: (e) => (isAiUnavailable(e) ? onUnavailable() : setError(errorMessage(e))),
  });

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (!customer) return setError('Escolha o cliente.');
    const parsed = aiDraftInput.safeParse({ kind, customerId: customer.id, tone, channel });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Confira os campos.');
    setDraft(null);
    run.mutate(parsed.data);
  };

  return (
    <div className="flex flex-col gap-5">
      <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
        <FormField label="Tipo de mensagem" className="sm:col-span-2">
          {(a) => <Select {...a} value={kind} onChange={(v) => setKind(v as Kind)} options={KINDS} className="h-11 sm:h-10" />}
        </FormField>

        <div className="sm:col-span-2">
          <CustomerPicker value={customer} onChange={setCustomer} searchable={canFindCustomers} />
        </div>

        <FormField label="Tom">
          {(a) => <Select {...a} value={tone} onChange={setTone} options={TONES} className="h-11 sm:h-10" />}
        </FormField>
        <FormField label="Canal">
          {(a) => <Select {...a} value={channel} onChange={(v) => setChannel(v as Channel)} options={CHANNELS} className="h-11 sm:h-10" />}
        </FormField>

        {error && (
          <p role="alert" className="rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive sm:col-span-2">
            {error}
          </p>
        )}
        <div className="sm:col-span-2">
          <Button type="submit" size="lg" className="w-full sm:w-auto" loading={run.isPending}>
            Gerar rascunho
          </Button>
        </div>
      </form>

      {draft && <DraftResult key={draft.body} draft={draft} />}
    </div>
  );
}

function DraftResult({ draft }: { draft: AiDraftResponse }) {
  const [subject, setSubject] = useState(draft.subject ?? '');
  const [body, setBody] = useState(draft.body);
  const allowed = draft.canContact[draft.channel];
  const hasPlaceholder = /\{\{[^}]+\}\}/.test(body);

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copiado`);
    } catch {
      toast.error('Não foi possível copiar. Selecione o texto e copie manualmente.');
    }
  };

  return (
    <section aria-label="Rascunho gerado" className="flex flex-col gap-4 rounded-panel border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-base font-semibold">Mensagem gerada</h3>
        <span className="rounded-full bg-sand px-2.5 py-1 text-xs font-semibold text-sand-foreground">Rascunho</span>
      </div>

      <Notice tone="warning">
        <strong className="font-semibold">{REVIEW_NOTICE}</strong>
      </Notice>

      {allowed ? (
        <Notice>O cliente permite contato por {CHANNEL_NAME[draft.channel]}.</Notice>
      ) : (
        <Notice tone="danger">
          <strong className="font-semibold">Não envie por {CHANNEL_NAME[draft.channel]}.</strong> Este cliente não deu consentimento para contato por esse canal. Use outro canal permitido ou peça o consentimento antes.
        </Notice>
      )}
      {draft.warnings.length > 0 && (
        <ul className="flex flex-col gap-1.5" aria-label="Alertas do rascunho">
          {draft.warnings.map((w) => (
            <li key={w}>
              <Notice tone="warning">{w}</Notice>
            </li>
          ))}
        </ul>
      )}
      {hasPlaceholder && <Notice>Os trechos entre chaves duplas, como {'{{nome}}'}, precisam ser preenchidos por você antes de usar.</Notice>}

      {draft.channel === 'email' && (
        <FormField label="Assunto">
          {(a) => <Input {...a} value={subject} onChange={(e) => setSubject(e.target.value)} className="h-11 sm:h-10" />}
        </FormField>
      )}
      <FormField label="Texto da mensagem">
        {(a) => <textarea {...a} value={body} onChange={(e) => setBody(e.target.value)} className={`${textareaClasses} min-h-44`} />}
      </FormField>

      <div className="flex flex-wrap gap-2">
        <Button className="h-11 flex-1 sm:h-10 sm:flex-none" onClick={() => void copy(body, 'Texto')}>
          <Copy className="size-4" aria-hidden />
          Copiar texto
        </Button>
        {draft.channel === 'email' && (
          <Button variant="secondary" className="h-11 flex-1 sm:h-10 sm:flex-none" onClick={() => void copy(subject, 'Assunto')}>
            Copiar assunto
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Você copia e envia pelo seu canal de costume. O sistema não envia nada por conta própria.</p>
    </section>
  );
}

function CustomerPicker({ value, onChange, searchable }: { value: { id: string; name: string } | null; onChange: (p: { id: string; name: string } | null) => void; searchable: boolean }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  const q = useQuery({
    queryKey: ['ai', 'customers', debounced],
    queryFn: ({ signal }) => aiApi.customers(debounced, signal),
    enabled: searchable && debounced.length >= 2 && !value,
  });

  if (!searchable) {
    return (
      <FormField label="Código do cliente" hint="Você não tem acesso à lista de clientes. Informe o código (UUID).">
        {(a) => <Input {...a} value={value?.id ?? ''} onChange={(e) => onChange(e.target.value ? { id: e.target.value.trim(), name: e.target.value.trim() } : null)} className="h-11 sm:h-10" />}
      </FormField>
    );
  }

  if (value) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium">Cliente</span>
        <div className="flex min-h-11 items-center justify-between gap-2 rounded-control border bg-muted px-3 text-sm">
          <span className="min-w-0 truncate font-medium">{value.name}</span>
          <button type="button" aria-label="Trocar cliente" onClick={() => { onChange(null); setTerm(''); }} className="flex size-11 shrink-0 items-center justify-center rounded-control text-muted-foreground hover:bg-card hover:text-foreground">
            <X className="size-4" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return (
    <FormField label="Cliente" hint="Digite pelo menos 2 letras do nome.">
      {(a) => (
        <div className="flex flex-col gap-2">
          <div className="relative">
            <MagnifyingGlass className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input {...a} type="search" value={term} onChange={(e) => setTerm(e.target.value)} className="h-11 pl-9 sm:h-10" autoComplete="off" />
          </div>
          {q.isFetching && <p role="status" className="text-xs text-muted-foreground">Buscando...</p>}
          {q.data && q.data.data.length === 0 && <p className="text-sm text-muted-foreground">Nenhum cliente encontrado.</p>}
          {q.isError && <p role="alert" className="text-sm text-destructive">{errorMessage(q.error)}</p>}
          {q.data && q.data.data.length > 0 && (
            <ul aria-label="Clientes encontrados" className="divide-y overflow-hidden rounded-control border bg-card">
              {q.data.data.map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => onChange({ id: p.id, name: p.name })} className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm hover:bg-muted">
                    <span className="min-w-0 truncate">{p.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </FormField>
  );
}
